'use client';

import { useParams, usePathname, useRouter } from 'next/navigation';

import mixpanel from 'mixpanel-browser';
import { SWRConfig, useSWRConfig } from 'swr';

import useUserRole from '@/hooks/user/useUserRole';

import { USER, USER_SOCIAL_PROFILES } from '@/lib/routes/apiEndpoints';
import { APP_ROUTER_ROUTES } from '@/lib/routes/appRoutes';

import { DOWNGRADE_FUNNEL, DOWNGRADE_FUNNEL_BLOCKED } from '@/routes/privateRoutes';
import { SIGN_IN } from '@/routes/publicRoutes';

type AppRouterSWRConfigWrapperProps = {
    children: React.ReactNode;
};
/**
 * Navigate to a route in App Router context
 * - App Router routes: use locale prefix in path (e.g., /en/signin)
 * - Pages Router routes: no locale prefix, use window.location for cross-router navigation
 */
const navigateToRoute = (
    path: string,
    locale: string | undefined,
    router: ReturnType<typeof useRouter>
) => {
    const isAppRouter = APP_ROUTER_ROUTES.some((route) => path === route);

    if (isAppRouter) {
        // App Router: localePrefix is 'never', so no locale prefix in URL
        router.push(path);
    } else {
        // Pages Router: use window.location for cross-router navigation
        window.location.href = path;
    }
};

/**
 * Check if current pathname is already on the target route
 */
const isAlreadyOnRoute = (pathname: string, targetRoute: string): boolean => {
    const pathWithoutLocale = pathname.replace(/^\/(en|fr|es)(?=\/|$)/, '') || '/';
    return pathWithoutLocale === targetRoute;
};

type HandleSWRErrorParams = {
    error: any;
    key: string;
    pathname: string;
    locale: string | undefined;
    isAdmin: boolean;
    mutate: ReturnType<typeof useSWRConfig>['mutate'];
    router: ReturnType<typeof useRouter>;
};

/**
 * Handle SWR errors globally: redirect to signin on auth errors,
 * or to the downgrade funnel on over-consumption errors.
 */
const handleSWRError = ({
    error,
    key,
    pathname,
    locale,
    isAdmin,
    mutate,
    router
}: HandleSWRErrorParams): void => {
    const isUnAuthenticated =
        error.response?.status === 401 && error.response?.data?.subcode === 'auth_unauthenticated';

    const cantFetchUserAndSocialProfiles =
        (key === USER || key === USER_SOCIAL_PROFILES) && error.response?.status === 500;

    if (isUnAuthenticated || cantFetchUserAndSocialProfiles) {
        if (isAlreadyOnRoute(pathname, SIGN_IN)) {
            return;
        }
        mutate(USER, false);
        mixpanel?.reset();
        navigateToRoute(SIGN_IN, locale, router);
        return;
    }

    const isOverConsumption =
        error.response?.status === 403 &&
        error.response?.data?.subcode === 'subscription_over_consumption';

    if (isOverConsumption) {
        if (isAdmin) {
            if (isAlreadyOnRoute(pathname, DOWNGRADE_FUNNEL)) {
                return;
            }
            navigateToRoute(DOWNGRADE_FUNNEL, locale, router);
            return;
        }

        if (isAlreadyOnRoute(pathname, DOWNGRADE_FUNNEL_BLOCKED)) {
            return;
        }
        navigateToRoute(DOWNGRADE_FUNNEL_BLOCKED, locale, router);
    }
};

const AppRouterSWRConfigWrapper = ({ children }: AppRouterSWRConfigWrapperProps) => {
    const router = useRouter();
    const params = useParams();
    const pathname = usePathname();
    const { isAdmin } = useUserRole();
    const { mutate } = useSWRConfig();

    const locale = params?.locale as string;

    const swrConfig = {
        onError: (error: any, key: string) =>
            handleSWRError({ error, key, pathname, locale, isAdmin, mutate, router })
    };

    return <SWRConfig value={swrConfig}>{children}</SWRConfig>;
};

export { AppRouterSWRConfigWrapper };
